import { toast } from '@/hooks/use-toast';
import { useMutation } from '@tanstack/react-query';
import { pdkAxios } from '../lib/pdk-axios';
import type { Notification } from '../models/registry';
import { notificationStore } from '../models/stores';

type Options = {
  notifications: Notification[];
};

type MutationContext = {
  snapshot: Notification[]; // full copy of notifications before mutation
  toRestoreIds: Set<string>; // which ones we attempted to delete
  count: number; // how many were deleted
};

export function useBulkDeleteNotifications({ notifications }: Options) {
  const removeLocal = (id: string) => {
    notificationStore.remove(id);
  };

  const restoreLocal = (n: Notification) => {
    notificationStore.upsert(n);
  };

  const createBulkDeleteMutation = (
    condition: (n: Notification) => boolean,
    endpoint: string,
  ) =>
    useMutation<void, Error, void, MutationContext>({
      onMutate: async () => {
        const toDelete = notifications.filter(condition);
        const toRestoreIds = new Set(toDelete.map((n) => n.id));
        const snapshot = notifications.slice();

        // optimistic remove
        toDelete.forEach((n) => removeLocal(n.id));

        return { snapshot, toRestoreIds, count: toDelete.length };
      },

      mutationFn: async () => {
        await pdkAxios.post(endpoint);
      },

      onSuccess: (_data, _vars, ctx) => {
        toast({
          title: 'Deleted',
          description: `${ctx.count} notification${ctx.count === 1 ? '' : 's'} removed.`,
        });
      },

      onError: (_err, _vars, ctx) => {
        ctx?.snapshot.forEach((n) => {
          if (ctx?.toRestoreIds.has(n.id)) {
            restoreLocal(n);
          }
        }),
          toast({
            title: 'Could not delete',
            description: 'Please try again.',
          });
      },
    });

  const deleteReadNotifications = createBulkDeleteMutation(
    (n) => n.isRead,
    '/notifications/delete-all-read',
  );

  const deleteDealCancelledNotifications = createBulkDeleteMutation(
    (n) => n.deal?.status === 'cancelled',
    '/notifications/delete-for-cancelled-deals',
  );

  const deleteDealDoneNotifications = createBulkDeleteMutation(
    (n) => n.deal?.status === 'done',
    '/notifications/delete-for-done-deals',
  );

  return {
    deleteReadNotifications,
    deleteDealCancelledNotifications,
    deleteDealDoneNotifications,
  };
}
